import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IQuote } from '@share/common-types';
import { AppThunk, isSpanish, Toaster, isMultiQuotePackage, isQuoteValid, hasAnyValidQuote, getAllPackageQuotes } from '@share/utils';
import { QuotesAPI } from '@share/api';

export interface IQuoteState {
  loading: boolean;
  errorHotels: string;
  errorCondos: string;
  result: {
    quotesHotels: IQuote[];
    quotesCondos: IQuote[];
    quotesCars: IQuote[];
    quotesActivities: IQuote[];
    quotesHistorical: IQuote[];
    quotesPackages: IQuote[];
    selectedQuotes: IQuote[];
  }
}

const initialState: IQuoteState = {
  loading: true,
  errorHotels: '',
  errorCondos: '',
  result: {
    quotesHotels: [],
    quotesCondos: [],
    quotesCars: [],
    quotesActivities: [],
    quotesHistorical: [],
    quotesPackages: [],
    selectedQuotes: []
  }
};

const quotesSlice = createSlice({
  name: 'quotes',
  initialState,
  reducers: {
    setLoading: (state: IQuoteState, { payload }: PayloadAction<boolean>) => {
      state.loading = payload;
    },
    setErrorHotels: (state: IQuoteState, { payload }: PayloadAction<string>) => {
      state.errorHotels = payload;
    },
    setErrorCondos: (state: IQuoteState, { payload }: PayloadAction<string>) => {
      state.errorCondos = payload;
    },
    setQuotesHotels: (state: IQuoteState, { payload }: PayloadAction<IQuote[]>) => {
      state.result.quotesHotels = payload;
    },
    setQuotesCondos: (state: IQuoteState, { payload }: PayloadAction<IQuote[]>) => {
      state.result.quotesCondos = payload;
    },
    setQuotesCars: (state: IQuoteState, { payload }: PayloadAction<IQuote[]>) => {
      state.result.quotesCars = payload;
    },
    setQuotesActivities: (state: IQuoteState, { payload }: PayloadAction<IQuote[]>) => {
      state.result.quotesActivities = payload;
    },
    setQuotesHistorical: (state: IQuoteState, { payload }: PayloadAction<IQuote[]>) => {
      state.result.quotesHistorical = payload;
    },
    setQuotesPackages: (state: IQuoteState, { payload }: PayloadAction<IQuote[]>) => {
      state.result.quotesPackages = payload;
    },
    setSelectedQuotes: (state: IQuoteState, { payload }: PayloadAction<IQuote[]>) => {
      state.result.selectedQuotes = payload;
    }
  },
});

export const quotesActions = quotesSlice.actions;
export const quotesReducer = quotesSlice.reducer;

const isPackage = (d: IQuote) => !!d.packageQuotes;

// A "package" that actually wraps a single quote (one category, one quoteBaseId — however
// many comparison options/locations it holds) is displayed in its specific tab instead of
// the Package tab. Only genuine multi-quote packages stay in the Package tab.
const isSingleQuotePackage = (d: IQuote) => isPackage(d) && !isMultiQuotePackage(d);

// Flatten a single-quote package into standard quotes: keep the wrapper's saved-quote
// metadata (id, creation date, comment, file) and take every nested option of its single
// category, so the existing classification below places it in the right tab and the
// tab/modal render and compare its options exactly like a regular quote.
const flattenSingleQuotePackage = (d: IQuote): IQuote[] => {
  const pq = d.packageQuotes;
  let inners: IQuote[] = [];
  let quoteType = '';
  let isCondo = false;
  if (pq?.hotelQuotes?.length) { inners = pq.hotelQuotes; quoteType = 'Hotel'; isCondo = false; }
  else if (pq?.condoQuotes?.length) { inners = pq.condoQuotes; quoteType = 'Condo'; isCondo = true; }
  else if (pq?.carQuotes?.length) { inners = pq.carQuotes; quoteType = 'Car'; isCondo = false; }

  return inners.map((inner) => ({
    ...d,
    ...inner,
    quoteBaseId: d.quoteBaseId,
    creationDate: d.creationDate || inner.creationDate,
    comment: d.comment || inner.comment,
    fileUrl: d.fileUrl || inner.fileUrl,
    quoteType,
    isCondo,
    packageQuotes: undefined,
  } as IQuote));
};

export const getQuotes = (id?: any): AppThunk => {
  return async (dispatch) => {
    dispatch(quotesActions.setLoading(true));
    dispatch(quotesActions.setQuotesHotels([]));
    dispatch(quotesActions.setQuotesCondos([]));
    dispatch(quotesActions.setQuotesCars([]));
    dispatch(quotesActions.setQuotesActivities([]));
    dispatch(quotesActions.setQuotesPackages([]));

    dispatch(quotesActions.setSelectedQuotes([]));

    try {
      const response = await QuotesAPI.get(null);
      if (response.status === 200) {
        const { data } = response;
        const mainData: IQuote[] = Array.isArray(data) ? data : (data?.data || []);

        const allPackages = mainData.filter(d => isPackage(d) && isMultiQuotePackage(d));
        const flattenedSingleQuotePackages = mainData.filter(d => isSingleQuotePackage(d)).flatMap(flattenSingleQuotePackage);
        const standardQuotes = [...mainData.filter(d => !isPackage(d)), ...flattenedSingleQuotePackages];

        // Expired quotes are no longer split into a separate Historical tab. Every quote
        // stays in its own type tab; a group (same quoteBaseId) whose quotes are all in the
        // past is flagged isExpired so the row/modal render a view-only action instead of
        // "View and reserve". The same rule applies to packages as a whole.
        const validBaseIds = new Set(standardQuotes.filter(isQuoteValid).map((d: any) => d.quoteBaseId));
        const flagExpired = (d: IQuote): IQuote => ({ ...d, isExpired: !validBaseIds.has(d.quoteBaseId) });

        const hotels = standardQuotes.filter((d: any) => d.quoteType === 'Hotel' && !d.isCondo).map(flagExpired);
        const condos = standardQuotes.filter((d: any) => d.isCondo).map(flagExpired);
        const cars = standardQuotes.filter((d: any) => d.quoteType === 'Car').map(flagExpired);
        const activities = standardQuotes.filter((d: any) => d.quoteType === 'Activity').map(flagExpired);

        const packages = allPackages.map((p): IQuote => ({ ...p, isExpired: !hasAnyValidQuote(getAllPackageQuotes(p)) }));

        dispatch(quotesActions.setQuotesHotels(hotels));
        dispatch(quotesActions.setQuotesCondos(condos));
        dispatch(quotesActions.setQuotesCars(cars));
        dispatch(quotesActions.setQuotesActivities(activities));
        dispatch(quotesActions.setQuotesPackages(packages));

        if (id) {
          dispatch(quotesActions.setSelectedQuotes(mainData?.filter((d: IQuote) => d.quoteBaseId === id) || []));
        }
      } else {
        dispatch(quotesActions.setErrorHotels(`[${response.status}] ${response.statusText}`));
        Toaster.error(!isSpanish() ? `An error occurred. [${response.status}] ${response.statusText}. If the problem persists, please contact the system administrator.`
          : `Ha ocurrido un error. [${response.status}] ${response.statusText}. Si el problema persiste, por favor contacte al administrador del sistema.`);
      }
    } catch (error: any) {
      console.error(error);
      dispatch(quotesActions.setErrorHotels(error.toString()));
      Toaster.error(!isSpanish() ? `An error occurred: ${error.toString()}. If the problem persists, please contact the system administrator.`
        : `Ha ocurrido un error. ${error.toString()}. Si el problema persiste, por favor contacte al administrador del sistema.`);
    }

    dispatch(quotesActions.setLoading(false));
  };
};


export const markQuotesAsRead = (quoteId?: any): AppThunk => {
  return async () => {
    try {
      await QuotesAPI.markQuotesAsRead(quoteId);
    } catch (error: any) {
      console.error(error);
    }
  };
};